import { expValEquals } from '@confluence/feature-experiments';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { query, writeQuery } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { graphql } from 'react-relay';

import useShareAndRestrictButtonDataQuery, {
	type useShareAndRestrictButtonDataQuery$data,
	type useShareAndRestrictButtonDataQuery$variables,
} from '../__generated__/useShareAndRestrictButtonDataQuery.graphql.ts';

import type {
	preloadShareAndRestrictButtonQuerySpaceOverviewQuery$data,
	preloadShareAndRestrictButtonQuerySpaceOverviewQuery$variables,
} from './__generated__/preloadShareAndRestrictButtonQuerySpaceOverviewQuery.graphql.ts';

export const preloadShareAndRestrictButtonQuerySpaceOverviewQuery = graphql`
	query preloadShareAndRestrictButtonQuerySpaceOverviewQuery($cloudId: ID!, $spaceKey: String!) {
		spaceHomepage(spaceKey: $spaceKey) {
			id
			hasInheritedRestrictions
			hasRestrictions
			hasViewRestrictions
			title
		}
		confluence_publicLinkSpaceHomePage(cloudId: $cloudId, spaceKey: $spaceKey)
			@optIn(to: "AskInCcApiPlatformBeforeUsing") {
			pageId
			pageStatus
		}
	}
`;

function transformSpaceHomepageData(data: any) {
	return {
		singleContent: data?.spaceHomepage || null,
		publicLinkPage: data?.confluence_publicLinkSpaceHomePage || null,
	};
}

export async function preloadShareAndRestrictButtonQuery(
	contentId?: string,
	spaceKey?: string,
): Promise<
	| {
			data:
				| preloadShareAndRestrictButtonQuerySpaceOverviewQuery$data
				| useShareAndRestrictButtonDataQuery$data
				| null;
			hasErrors: boolean;
	  }
	| undefined
> {
	const { isLoggedIn, cloudId } = getPreloaderFnContext();

	if (!isLoggedIn) {
		return;
	}

	if (
		!contentId &&
		spaceKey &&
		expValEquals('fix_preload_share_and_restrict_query_chaining_', 'cohort', 'test')
	) {
		const result = await query<
			preloadShareAndRestrictButtonQuerySpaceOverviewQuery$data,
			preloadShareAndRestrictButtonQuerySpaceOverviewQuery$variables
		>({
			query: preloadShareAndRestrictButtonQuerySpaceOverviewQuery,
			variables: { cloudId, spaceKey },
			errorPolicy: 'all',
		});

		if (result.hasErrors) {
			return result;
		}

		const data = result.data;

		const homepageId = data?.spaceHomepage?.id;
		if (!homepageId) {
			return;
		}

		const transformedData = transformSpaceHomepageData(data);

		// Prime Apollo cache so the button component finds its data already cached
		writeQuery<
			useShareAndRestrictButtonDataQuery$data,
			useShareAndRestrictButtonDataQuery$variables
		>({
			query: useShareAndRestrictButtonDataQuery,
			variables: {
				contentId: homepageId,
				isContentTypePublicLinkEligible: true,
			},
			data: transformedData,
		});

		return;
	}

	if (contentId) {
		const result = await query<
			useShareAndRestrictButtonDataQuery$data,
			useShareAndRestrictButtonDataQuery$variables
		>({
			query: useShareAndRestrictButtonDataQuery,
			variables: {
				contentId,
				isContentTypePublicLinkEligible: true,
			},
			errorPolicy: 'all',
		});

		if (result.hasErrors) {
			return result;
		}

		return;
	}

	// eslint-disable-next-line no-console
	console.error('Either contentId or spaceKey must be provided');
	return;
}
